import React, { useContext, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { IconButton } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import Tooltip from '@material-ui/core/Tooltip';
import AutorenewIcon from '@material-ui/icons/Autorenew';
import cx from 'classnames';
import { TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL } from 'config/urls';
import { AssignmentStatusOptions } from 'constants/assignments';
import {
  preannotationsRetry,
  useTrackerInstanceSectionPreAnnotationsWithMaxRetries
} from 'sdk';

import Icons from 'pages/Teachers/Assignments/Feedback/components/RightMenu/Icons';
import { ReactComponent as InfoIcon } from 'pages/Teachers/ClassroomInsights/assets/info-icon.svg';
import { ReactComponent as FilterIcon } from 'pages/Teachers/ClassroomInsights/assets/sort-icon.svg';
import BulkActions from 'pages/Teachers/ClassroomInsights/components/BulkActions';
import StudentBulkEditDialog from 'pages/Teachers/ClassroomInsights/components/StudentBulkEditDialog';
import StudentBulkPublishDialog from 'pages/Teachers/ClassroomInsights/components/StudentBulkPublishDialog';
import StudentProgressBox from 'pages/Teachers/ClassroomInsights/components/StudentProgressBox';
import StudentWorkDialog from 'pages/Teachers/ClassroomInsights/components/StudentWorkDialog';
import {
  MASTERY_LEVELS,
  MASTERY_LEVEL_TO_LABEL_MAPPER
} from 'pages/Teachers/ClassroomInsights/constants';
import {
  getRubricLabelByValue,
  shouldDisablePublish
} from 'pages/Teachers/ClassroomInsights/utils';
import { trackMixpanelEvent } from 'utils/integrations/mixpanel';
import { notifyErrors, notifySuccess } from 'utils/notifications';

import StudentsSearch from './components/StudentsSearch';
import UserContext from 'components/UserContext';

import styles from './style.module.scss';

const StudentProgress = ({
  students,
  loadingCount,
  allStudents,
  isLoadingData,
  onActionSuccess,
  sectionId,
  objectiveValueType,
  filters,
  setFilters
}) => {
  const { user } = useContext(UserContext);
  const { trackerInstanceSectionId } = useParams();

  const [activeStudentTagAssignmentId, setActiveStudentTagAssignmentId] =
    useState(null);

  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [isBulkEditDialogOpen, setIsBulkEditDialogOpen] = useState(false);
  const [isBulkPublishDialogOpen, setIsBulkPublishDialogOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);

  const {
    data: preAnnotationsData,
    isLoading: isPreAnnotationsLoading,
    refetch: refetchPreAnnotations
  } = useTrackerInstanceSectionPreAnnotationsWithMaxRetries({
    trackerInstanceSectionId
  });

  const viewableStudents = useMemo(
    () =>
      students?.filter(
        (student) => student.student_tag_assignment_id !== null
      ) || [],
    [students]
  );

  const selectableStudentIds = viewableStudents
    .filter((student) => student.tag_description !== MASTERY_LEVELS.LOADING)
    .map((student) => student.student_tag_assignment_id);

  const allStudentsSelected =
    selectedStudentIds.length === selectableStudentIds.length &&
    selectableStudentIds.length > 0;

  const selectedStudentNames = useMemo(() => {
    if (selectedStudentIds.length === 0) return [];

    return selectedStudentIds.reduce((names, id) => {
      const student = viewableStudents.find(
        (s) => s.student_tag_assignment_id === id
      );

      if (student && student.student_name) {
        names.push(student.student_name);
      }
      return names;
    }, []);
  }, [selectedStudentIds, viewableStudents]);

  const studentOptions = useMemo(
    () =>
      allStudents?.map((student) => ({
        student_id: student.student_id,
        name: student.student_name,
        grade: getRubricLabelByValue({
          value: student.student_work_score,
          objectiveValueType
        })
      })) || [],
    [allStudents, objectiveValueType]
  );

  const selectedStudents = viewableStudents.filter((student) =>
    selectedStudentIds.includes(student.student_tag_assignment_id)
  );

  const studentsToPublish = [];
  const studentsNeedsReview = [];

  for (const student of selectedStudents) {
    if (
      student.tracker_score_student_status ===
        AssignmentStatusOptions.COMPLETE ||
      student.tracker_score_student_status === AssignmentStatusOptions.REVISE
    ) {
      continue;
    }

    if (
      student.tag_description === MASTERY_LEVELS.NEEDS_REVIEW ||
      student.tag_description === MASTERY_LEVELS.LOADING
    ) {
      studentsNeedsReview.push(student);
    } else {
      studentsToPublish.push(student);
    }
  }

  const currentStudent = useMemo(() => {
    return activeStudentTagAssignmentId
      ? viewableStudents.find(
          (s) => s.student_tag_assignment_id === activeStudentTagAssignmentId
        )
      : null;
  }, [activeStudentTagAssignmentId, viewableStudents]);

  const activeStudentIndex = useMemo(() => {
    if (!currentStudent) return null;

    return viewableStudents.findIndex(
      (s) =>
        s.student_tag_assignment_id === currentStudent.student_tag_assignment_id
    );
  }, [currentStudent, viewableStudents]);

  const handleStudentBoxSelect = ({ id }) => {
    if (!id) return;

    const isCurrentlyEmpty = selectedStudentIds.length === 0;
    const isSelecting = !selectedStudentIds.includes(id);

    if (isCurrentlyEmpty && isSelecting) {
      trackMixpanelEvent(user.id, 'Multi-select started', {
        feature: 'live classroom insights',
        view: 'student progress',
        button: 'student work select',
        class_id: sectionId,
        normalizedURL:
          process.env.REACT_APP_BASE_FRONTEND_URL +
          TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL
      });
    }

    if (!isCurrentlyEmpty) {
      trackMixpanelEvent(user.id, 'Students selected count changed', {
        feature: 'live classroom insights',
        view: 'student progress',
        button: 'student work select',
        class_id: sectionId,
        normalizedURL:
          process.env.REACT_APP_BASE_FRONTEND_URL +
          TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL
      });
    }

    if (selectedStudentIds.includes(id)) {
      setSelectedStudentIds((prev) =>
        prev.filter((studentId) => studentId !== id)
      );
    } else {
      setSelectedStudentIds((prev) => [...prev, id]);
    }
  };

  const handleToggleSelectAll = () => {
    if (allStudentsSelected) {
      setSelectedStudentIds([]);
    } else {
      const isCurrentlyEmpty = selectedStudentIds.length === 0;

      if (isCurrentlyEmpty) {
        trackMixpanelEvent(user.id, 'Multi-select started', {
          feature: 'live classroom insights',
          view: 'student progress',
          button: 'select all student works',
          class_id: sectionId,
          normalizedURL:
            process.env.REACT_APP_BASE_FRONTEND_URL +
            TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL
        });
      }

      setSelectedStudentIds(selectableStudentIds);
    }
  };

  const handleBoxClick = ({ student }) => {
    setActiveStudentTagAssignmentId(student.student_tag_assignment_id);

    trackMixpanelEvent(user.id, 'Student tile clicked', {
      feature: 'live classroom insights',
      view: 'student progress',
      button: 'student tile',
      class_id: sectionId,
      normalizedURL:
        process.env.REACT_APP_BASE_FRONTEND_URL +
        TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL
    });
  };

  const handleBulkEditConfirmed = () => {
    onActionSuccess();

    trackMixpanelEvent(user.id, 'Bulk grade publish confirmed', {
      feature: 'live classroom insights',
      view: 'Override Levels dialog',
      button: 'Confirm changes',
      class_id: sectionId,
      normalizedURL:
        process.env.REACT_APP_BASE_FRONTEND_URL +
        TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL
    });
  };

  const handleEditClick = () => {
    setIsBulkEditDialogOpen(true);

    trackMixpanelEvent(user.id, 'Bulk grade edit initiated', {
      feature: 'live classroom insights',
      view: 'student progress',
      button: 'bulk edit student works',
      class_id: sectionId,
      normalizedURL:
        process.env.REACT_APP_BASE_FRONTEND_URL +
        TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL
    });
  };

  const handleEditConfirmed = () => {
    onActionSuccess();

    trackMixpanelEvent(user.id, 'Bulk grade edit confirmed', {
      feature: 'live classroom insights',
      view: 'Override Levels dialog',
      button: 'Confirm changes',
      class_id: sectionId,
      normalizedURL:
        process.env.REACT_APP_BASE_FRONTEND_URL +
        TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL
    });
  };

  const hasFilters = filters.studentIds.length > 0;
  const hasSelectedStudents = selectedStudentIds.length > 0;

  const handlePreannotationsRetry = async () => {
    trackMixpanelEvent(user.id, `Pre-annotations retry initiated`, {
      feature: 'live classroom insights',
      view: 'student progress',
      button: 'Reupload needed Retry',
      class_id: sectionId,
      numberOfPreAnnotationsForRetry: preAnnotationsData.length,
      normalizedURL:
        process.env.REACT_APP_BASE_FRONTEND_URL +
        TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL
    });

    const { success } = await preannotationsRetry({
      preannotationIds: preAnnotationsData.map((pa) => pa.id)
    });
    if (success) {
      onActionSuccess();
      refetchPreAnnotations();
      notifySuccess('Pre-annotations retry initiated successfully.');
    } else {
      notifyErrors('Failed to initiate pre-annotations retry.');
    }
  };

  return (
    <>
      <header className={styles.progressHeader}>
        <div className={styles.headingContainer}>
          <Tooltip title="AI provides an initial evaluation. Teachers can adjust the status for any student.">
            <InfoIcon />
          </Tooltip>
          <h3 className={styles.heading}>Students Progress</h3>
        </div>

        <div className={styles.filtersContainer}>
          <StudentsSearch
            students={studentOptions}
            sectionId={sectionId}
            nameValue={filters.studentIds}
            levelValue={filters.masteryLevel}
            gradeValue={filters.grade}
            submissionStatusValue={filters.submission_status}
            onStudentSelect={(studentIds) =>
              setFilters({ ...filters, studentIds: studentIds })
            }
            objectiveValueType={objectiveValueType}
            onLevelSelect={(level) =>
              setFilters({ ...filters, masteryLevel: level })
            }
            onGradeSelect={(grade) => setFilters({ ...filters, grade: grade })}
            onSubmissionStatusSelect={(status) =>
              setFilters({ ...filters, submission_status: status })
            }
          />
          <IconButton
            className={styles.filterButton}
            onClick={() => setSortOpen((prev) => !prev)}
          >
            <FilterIcon />
            {sortOpen && (
              <div className={styles.sortOptions}>
                <div
                  key={0}
                  className={cx(styles.result, {
                    [styles.active]: filters.sort_by === 'name'
                  })}
                  onClick={() => {
                    localStorage.setItem('liveClassroomInsightsSort', 'name');
                    setFilters((prev) => ({ ...prev, sort_by: 'name' }));
                    trackMixpanelEvent(user.id, 'Sort changed', {
                      feature: 'live classroom insights',
                      view: 'student progress',
                      button: 'sort by name',
                      class_id: sectionId,
                      normalizedURL:
                        process.env.REACT_APP_BASE_FRONTEND_URL +
                        TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL
                    });
                  }}
                >
                  {filters.sort_by === 'name' && (
                    <Icons.Check fontSize="small" />
                  )}{' '}
                  Sort by name
                </div>
                <div
                  key={1}
                  className={cx(styles.result, {
                    [styles.active]: filters.sort_by === 'level'
                  })}
                  onClick={() => {
                    localStorage.setItem('liveClassroomInsightsSort', 'level');
                    setFilters((prev) => ({ ...prev, sort_by: 'level' }));
                    trackMixpanelEvent(user.id, 'Sort changed', {
                      feature: 'live classroom insights',
                      view: 'student progress',
                      button: 'sort by level',
                      class_id: sectionId,
                      normalizedURL:
                        process.env.REACT_APP_BASE_FRONTEND_URL +
                        TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL
                    });
                  }}
                >
                  {filters.sort_by === 'level' && (
                    <Icons.Check fontSize="small" />
                  )}{' '}
                  Sort by level
                </div>
              </div>
            )}
          </IconButton>
          <Tooltip title="Reupload needed Retry">
            <IconButton
              className={styles.refreshButton}
              disabled={
                preAnnotationsData?.length <= 0 ||
                isPreAnnotationsLoading ||
                isLoadingData ||
                loadingCount > 0
              }
              onClick={handlePreannotationsRetry}
            >
              <AutorenewIcon onClick={() => {}} />
            </IconButton>
          </Tooltip>
        </div>
      </header>
      <BulkActions
        hasFilters={hasFilters}
        hasSelectedStudents={hasSelectedStudents}
        allStudentsSelected={allStudentsSelected}
        onEditClick={() => handleEditClick()}
        onSelectAll={() => handleToggleSelectAll()}
        onPublishClick={() => {
          setIsBulkPublishDialogOpen(true);
        }}
      />

      <div className={styles.progressDashboard}>
        {isLoadingData && <CircularProgress />}
        {students?.map((student, index) => {
          const isPublished = shouldDisablePublish({
            status: student.tracker_score_student_status,
            overallResponseStatus: student.tag_description
          });
          return (
            <StudentProgressBox
              key={`studentprogress-${index}`}
              name={student.student_name}
              status={MASTERY_LEVEL_TO_LABEL_MAPPER[student.tag_description]}
              masteryLabel={student.tag_description}
              grade={getRubricLabelByValue({
                value: student.student_work_score,
                objectiveValueType
              })}
              isPublished={isPublished}
              isSelected={selectedStudentIds.includes(
                student.student_tag_assignment_id
              )}
              onClick={() => handleBoxClick({ student })}
              onSelect={() =>
                handleStudentBoxSelect({
                  id: student.student_tag_assignment_id
                })
              }
            />
          );
        })}
        {isBulkEditDialogOpen && selectedStudentIds.length > 0 && (
          <StudentBulkEditDialog
            onClose={() => {
              setIsBulkEditDialogOpen(false);
            }}
            onEdit={() => {
              setSelectedStudentIds([]);
            }}
            selectedStudentIds={selectedStudentIds}
            affectedStudentNames={selectedStudentNames}
            onActionSuccess={handleEditConfirmed}
            objectiveValueType={objectiveValueType}
            sectionId={sectionId}
          />
        )}

        {isBulkPublishDialogOpen && selectedStudentIds.length > 0 && (
          <StudentBulkPublishDialog
            studentsToPublish={studentsToPublish}
            studentsNeedsReview={studentsNeedsReview}
            onClose={() => {
              setIsBulkPublishDialogOpen(false);
            }}
            onPublish={() => {
              setSelectedStudentIds([]);
            }}
            onActionSuccess={handleBulkEditConfirmed}
          />
        )}

        {activeStudentIndex !== null && (
          <StudentWorkDialog
            currentStudent={currentStudent}
            objectiveValueType={objectiveValueType}
            status={
              MASTERY_LEVEL_TO_LABEL_MAPPER[currentStudent.tag_description]
            }
            onClose={() => {
              setActiveStudentTagAssignmentId(null);
            }}
            onNext={() => {
              setActiveStudentTagAssignmentId(
                viewableStudents[
                  Math.min(activeStudentIndex + 1, viewableStudents.length - 1)
                ].student_tag_assignment_id
              );
            }}
            onPrev={() =>
              setActiveStudentTagAssignmentId(
                viewableStudents[Math.max(activeStudentIndex - 1, 0)]
                  .student_tag_assignment_id
              )
            }
            onActionSuccess={onActionSuccess}
            sectionId={sectionId}
          />
        )}
      </div>
    </>
  );
};

export default StudentProgress;
